import { useMemo } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { ReportInternalType, ReportStatus, useAllReportsListQuery } from '@/__generated__/graphql';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state/atoms';
import { actionStatusDisplayToEnum } from '@/utils';
import { useAuth0 } from '@auth0/auth0-react';

export const useFilteredReportsByType = (
  type: ReportInternalType,
  status: ReportStatus | 'ALL',
  searchTerm?: string,
  groupId?: string,
  groupName?: string
) => {
  const selectedReportFilters = useAtomValue(state.selectedReportFilters);
  const createdByMe = useAtomValue(state.createdByMeFilter);
  const structureId = useGetStructureId();
  const { user } = useAuth0();

  const selectedAssemblyTemplateFilters = useMemo(() => {
    if (type !== ReportInternalType.Component) return [];

    return Object.entries(selectedReportFilters)
      .filter(([key]) => key !== 'actionStatus')
      .map(([key, value]) => ({
        id: key,
        value,
      }));
  }, [selectedReportFilters, type]);

  const reportFilters = useMemo(() => {
    return Object.entries(selectedReportFilters)
      .filter(([key]) => key === 'actionStatus')
      .map(([key, values]) => ({
        id: key,
        value: values.map((value) => actionStatusDisplayToEnum[value] ?? value),
      }));
  }, [selectedReportFilters]);

  const { data, loading, error } = useAllReportsListQuery({
    variables: {
      input: {
        structureId,
        status: status === 'ALL' ? undefined : status,
        internalType: type,
        name: searchTerm,
        groupId,
        groupName,
        ...(type === ReportInternalType.Component && {
          selectedAssemblyTemplateFilters,
        }),
        selectedReportFilters: reportFilters,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const filteredReports = useMemo(() => {
    const reports = data?.allReports ?? [];
    if (createdByMe && (user?.email || user?.name)) {
      return reports.filter(
        (report) =>
          report?.createdByUser?.email === user.email &&
          report?.createdByUser?.name === user.name
      );
    }
    return reports;
  }, [data?.allReports, createdByMe, user?.email, user?.name]);

  return { reports: filteredReports, loading, error, allReportsData: data };
};
